import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Button, Flex, FormField, IllustratedMessage, Radio, Spacer, Typography } from '@percent/lemonade'

import { useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks'
import { SurveyEventName } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/causeDashboardAnalytics.types'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useMutation } from '@percent/cause-dashboard/common/hooks'
import type {
  SurveyProductDetail,
  SurveyResponseValue
} from '@percent/cause-dashboard/services/surveys/surveyService.types'

const ALREADY_RESPONDED_ERROR_CODE = 'product_activation_survey/already_responded'

const SURVEY_RESPONSE_VALUES: SurveyResponseValue[] = ['yes', 'no_struggling', 'no_uninterested', 'no_later']

const SURVEY_RESPONSE_I18N_KEYS: Record<SurveyResponseValue, string> = {
  yes: 'survey.discountRedemption.responses.yes',
  no_struggling: 'survey.discountRedemption.responses.noStruggling',
  no_uninterested: 'survey.discountRedemption.responses.noUninterested',
  no_later: 'survey.discountRedemption.responses.noLater'
}

const VALID_RESPONSES = new Set<string>(SURVEY_RESPONSE_VALUES)

export type DiscountRedemptionSurveyFormProps = {
  products: SurveyProductDetail[]
  token: string
  queryParams: { validationSubmissionId: string | null; response: string | null }
  onSuccess: () => void
}

export function DiscountRedemptionSurveyForm({
  products,
  token,
  queryParams,
  onSuccess
}: DiscountRedemptionSurveyFormProps) {
  const { t } = useTranslation()
  const { surveyService } = useServices()
  const analytics = useCausesPortalAnalytics()

  const [formState, setFormState] = useState<Record<string, SurveyResponseValue>>({})

  useEffect(() => {
    const initialState: Record<string, SurveyResponseValue> = {}
    const queryResponse =
      queryParams.response && VALID_RESPONSES.has(queryParams.response)
        ? (queryParams.response as SurveyResponseValue)
        : null

    for (const product of products) {
      if (queryParams.validationSubmissionId === product.validationSubmissionId && queryResponse) {
        initialState[product.validationSubmissionId] = queryResponse
      } else if (product.response) {
        initialState[product.validationSubmissionId] = product.response
      }
    }

    setFormState(initialState)
  }, [products, queryParams.validationSubmissionId, queryParams.response])

  const allAnswered = products.every(p => formState[p.validationSubmissionId] != null)

  const [{ isLoading: isSubmitting, errorMessage: submitErrorMessage, error: submitError }, { apiFunc: submitSurvey }] =
    useMutation(
      (payload: { responses: { validationSubmissionId: string; response: SurveyResponseValue }[] }) =>
        surveyService.submitSurveyResponses(token, payload),
      () => {
        analytics?.track?.(SurveyEventName.DISCOUNT_REDEMPTION_SURVEY_SUBMITTED, {
          productCount: products.length,
          token
        })
        onSuccess()
      }
    )

  const handleSubmit = () => {
    submitSurvey({
      responses: products.map(p => ({
        validationSubmissionId: p.validationSubmissionId,
        response: formState[p.validationSubmissionId] as SurveyResponseValue
      }))
    })
  }

  const setResponse = (validationSubmissionId: string, response: SurveyResponseValue) => {
    setFormState(prev => ({ ...prev, [validationSubmissionId]: response }))
  }

  const isAlreadyResponded = submitError?.code === ALREADY_RESPONDED_ERROR_CODE

  if (submitErrorMessage) {
    return (
      <Flex justify="center" align="center" mih="100vh" p="xl">
        <IllustratedMessage
          illustration={isAlreadyResponded ? 'nothing-to-see' : 'general-error'}
          title={t(
            isAlreadyResponded
              ? 'survey.discountRedemption.errors.titleAlreadyResponded'
              : 'survey.discountRedemption.errors.title'
          )}
          description={t(
            isAlreadyResponded
              ? 'survey.discountRedemption.errors.messageAlreadyResponded'
              : 'survey.discountRedemption.errors.messageSubmit'
          )}
          cta={
            isAlreadyResponded
              ? undefined
              : {
                  label: t('survey.discountRedemption.actions.refresh'),
                  onClick: () => window.location.reload()
                }
          }
        />
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="lg" p="xl" maw={720} mx="auto">
      <Typography variant="h3">{t('survey.discountRedemption.title')}</Typography>
      <Typography variant="default">{t('survey.discountRedemption.description')}</Typography>

      {products.map(product => (
        <Box key={product.validationSubmissionId}>
          <FormField label={product.configurationName} necessity="required" data-testid="survey-product">
            <Flex direction="column" gap="xs">
              {SURVEY_RESPONSE_VALUES.map(value => (
                <Radio
                  key={value}
                  id={`${product.validationSubmissionId}-${value}`}
                  name={`survey-${product.validationSubmissionId}`}
                  label={t(SURVEY_RESPONSE_I18N_KEYS[value] as keyof typeof t)}
                  value={value}
                  checked={formState[product.validationSubmissionId] === value}
                  onChange={() => setResponse(product.validationSubmissionId, value)}
                  data-testid={`survey-radio-${value}`}
                />
              ))}
            </Flex>
          </FormField>
        </Box>
      ))}

      <Spacer size="m" />
      <Button onClick={handleSubmit} disabled={!allAnswered || isSubmitting} data-testid="survey-submit">
        {isSubmitting
          ? t('survey.discountRedemption.actions.submitting')
          : t('survey.discountRedemption.actions.submit')}
      </Button>
    </Flex>
  )
}
